/* eslint-disable @next/next/no-img-element */
'use client'

import { AlertCircle, CheckCircle2 } from 'lucide-react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import * as React from 'react'

import { useToast } from '@/hooks/use-toast'

import { NON_FSDS_DEFAULT_LOGO_URL } from '@/configs/non-fsds-config'
import { TEMPLATE_DEFAULT_URLS } from '@/configs/template-default-urls'
import authService from '@/services/auth.service'
import { isErrorAxios } from '@/utils/axios-error-handle'

export interface NonFsdsEmailVerificationSectionProps {
  logoUrl: string
  brandHref?: string
  signInUrl?: string
  errorButtonText?: string
  successButtonText?: string
}

const StateLoading = () => (
  <div className="flex flex-col items-center justify-center space-y-4">
    <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600" />
    <h3 className="text-xl font-semibold text-slate-900">Verifying...</h3>
    <p className="text-center text-sm text-slate-500">Please wait while we verify your email.</p>
  </div>
)

const StateError = ({ signInUrl, buttonText }: { signInUrl: string; buttonText: string }) => (
  <div className="flex flex-col items-center justify-center space-y-4">
    <div className="mb-2 rounded-full bg-blue-50 p-3">
      <AlertCircle className="h-10 w-10 text-blue-500" />
    </div>
    <h3 className="text-xl font-semibold text-slate-900">Verification Failed</h3>
    <p className="text-center text-sm text-slate-500">Your session has expired or the token is invalid.</p>
    <Link
      href={signInUrl}
      className="mt-4 flex w-full items-center justify-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
    >
      {buttonText}
    </Link>
  </div>
)

const StateSuccess = ({ signInUrl, buttonText }: { signInUrl: string; buttonText: string }) => (
  <div className="flex flex-col items-center justify-center space-y-4">
    <div className="mb-2 rounded-full bg-green-50 p-3">
      <CheckCircle2 className="h-10 w-10 text-green-500" />
    </div>
    <h3 className="text-xl font-semibold text-slate-900">Email Verified!</h3>
    <p className="text-center text-sm text-slate-500">Your email has been successfully verified. You can now log in.</p>
    <Link
      href={signInUrl}
      className="mt-4 flex w-full items-center justify-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
    >
      {buttonText}
    </Link>
  </div>
)

export default function NonFsdsEmailVerificationSection({
  logoUrl = NON_FSDS_DEFAULT_LOGO_URL,
  brandHref = TEMPLATE_DEFAULT_URLS.home,
  signInUrl = TEMPLATE_DEFAULT_URLS.signIn,
  errorButtonText = 'Back to Sign In',
  successButtonText = 'Proceed to Sign In',
}: NonFsdsEmailVerificationSectionProps) {
  const [isVerified, setIsVerified] = React.useState(false)
  const [hasError, setHasError] = React.useState(false)
  const [loaded, setLoaded] = React.useState(false)

  const searchParams = useSearchParams()
  const token = searchParams.get('token')
  const { toast } = useToast()

  React.useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setHasError(true)
        setLoaded(true)
        return
      }

      try {
        const res = await authService.verifyAccount({ token })
        setIsVerified(Boolean(res?.success))
      } catch (err: unknown) {
        setHasError(true)

        if (isErrorAxios(err)) {
          toast({
            variant: 'destructive',
            title: 'Uh oh! Something went wrong.',
            description: 'Error verifying email!',
          })
        }
      } finally {
        setLoaded(true)
      }
    }

    void verifyEmail()
  }, [token, toast])

  return (
    <div className="relative flex min-h-svh flex-col items-center justify-center overflow-hidden bg-slate-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="pointer-events-none absolute left-0 top-0 h-full w-full bg-[radial-gradient(circle_at_top_left,_var(--tw-gradient-stops))] from-blue-100 via-transparent to-transparent opacity-40" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
      <div className="pointer-events-none absolute left-10 top-20 h-72 w-72 rounded-full bg-blue-300/10 blur-3xl" />

      <div className="relative z-10 w-full max-w-md rounded-3xl border border-slate-100/50 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:p-12">
        <div className="mb-8 flex justify-center">
          <Link href={brandHref} className="inline-block transition-transform duration-200 hover:scale-105 active:scale-95">
            <img src={logoUrl} alt="Logo" className="h-10 w-auto" />
          </Link>
        </div>

        <div className="w-full">
          {!loaded && <StateLoading />}
          {loaded && hasError && <StateError signInUrl={signInUrl} buttonText={errorButtonText} />}
          {loaded && !hasError && isVerified && <StateSuccess signInUrl={signInUrl} buttonText={successButtonText} />}
        </div>
      </div>
    </div>
  )
}
